import { arrow, autoUpdate, computePosition, flip, offset, shift, size } from "@floating-ui/dom";
const ARROW_SIZE = 8;
export function useTooltipOptions(tooltip, options = {}) {
    options = {
        placement: 'bottom',
        middleware: [
            shift({
                mainAxis: true,
                padding: 16,
            }),
            size(),
            flip(),
            offset(8),
        ],
        ...options,
    };
    const arrowEl = tooltip.querySelector('[data-arrow]');
    if (arrowEl) {
        options.middleware.push(arrow({ element: arrowEl }));
    }
    return options;
}
const registry = [];
const closeClickHandlerAdded = false;
function addCloseClickHandler() {
    if (closeClickHandlerAdded) {
        return;
    }
    document.addEventListener('click', (e) => {
        for (const tooltip of registry.filter(tooltip => tooltip.shown)) {
            if (tooltip.element.contains(e.target) || !tooltip.tooltip.contains(e.target)) {
                tooltip.hide();
            }
        }
    });
}
function closeAllShownTooltips() {
    for (const tooltip of registry.filter(tooltip => tooltip.shown)) {
        tooltip.hide();
    }
}
export class Tooltip {
    constructor(element, tooltip, options = {}) {
        this.element = element;
        this.tooltip = tooltip;
        this.options = useTooltipOptions(tooltip, options);
        this.arrowElement = this.tooltip.querySelector('[data-arrow]');
    }
    attachClickEvents() {
        registry.push(this);
        addCloseClickHandler();
        this.element.addEventListener('click', this.onclick.bind(this));
    }
    attachHoverEvents() {
        this.element.addEventListener('mouseenter', this.show.bind(this));
        this.element.addEventListener('mouseleave', this.hide.bind(this));
    }
    destroy() {
        this.element.removeEventListener('click', this.onclick.bind(this));
        this.element.removeEventListener('mouseenter', this.show.bind(this));
        this.element.removeEventListener('mouseleave', this.hide.bind(this));
        const index = registry.findIndex(tooltip => tooltip === this);
        if (index !== -1) {
            delete registry[index];
        }
    }
    get shown() {
        return !!this.cleanup;
    }
    show() {
        closeAllShownTooltips();
        this.tooltip.style.display = 'block';
        this.cleanup = autoUpdate(document.documentElement, this.tooltip, () => {
            computePosition(this.element, this.tooltip, this.options)
                .then(({ x, y, placement, middlewareData }) => {
                const placementSide = placement.split('-')[0];
                this.tooltip.style.left = `${x}px`;
                this.tooltip.style.top = `${y}px`;
                if (!this.arrowElement || !middlewareData.arrow) {
                    return;
                }
                const { x: arrowX, y: arrowY } = middlewareData.arrow;
                const staticSide = {
                    top: 'bottom',
                    right: 'left',
                    bottom: 'top',
                    left: 'right',
                }[placementSide];
                Object.assign(this.arrowElement.style, {
                    left: arrowX != null ? `${arrowX}px` : '',
                    top: arrowY != null ? `${arrowY}px` : '',
                    right: '',
                    bottom: '',
                    [staticSide]: `-${ARROW_SIZE / 2}px`, // половина размера стрелки
                    transform: placementSide === 'top' ? 'rotate(180deg)' : '',
                });
            });
        }, { animationFrame: true });
    }
    hide() {
        this.tooltip.style.display = '';
        if (this.cleanup) {
            this.cleanup();
            this.cleanup = null;
        }
    }
    toggle() {
        if (this.shown) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    onclick(event) {
        event.stopPropagation();
        event.preventDefault();
        this.toggle();
    }
}
const hintSelector = '.help-box';
const hintTooltipSelector = '.help-box__hint';
const tooltipButtonSelector = '.tooltip-btn';
const tooltipSelector = '.tooltip';
export default function initTooltips() {
    document.querySelectorAll(hintSelector)
        .forEach((element) => {
        new Tooltip(element, element.querySelector(hintTooltipSelector)).attachHoverEvents();
    });
    document.querySelectorAll(tooltipButtonSelector)
        .forEach(trigger => {
        new Tooltip(trigger, trigger.parentElement.querySelector(tooltipSelector)).attachClickEvents();
    });
}
